import {h, Fragment} from "preact"
import { useAppStateSelector, useSort, pick } from "@nosto/preact"
import { useState, useEffect } from "preact/hooks"
import { sortOptions } from "../config"
import { Icon, Select } from "./elements"
import { toggleButtonId } from "./Sidebar"

// Helper to check if desktop
const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 1024

export default function ActionToolbar() {
    const { loading, query, response, pageType } = useAppStateSelector(state =>
        pick(state, "loading", "query", "response", "pageType")
    )

    const [isNewLayout, setIsNewLayout] = useState(false)
    const [gridColumns, setGridColumns] = useState(() => isDesktop() ? 2 : 1) // Default: desktop=2, mobile=1

    useEffect(() => {
        if (typeof window !== 'undefined' && window.isNewLayout) {
            setIsNewLayout(true)
        }

        // Set initial value based on screen size
        setGridColumns(isDesktop() ? 2 : 1)

        // Dispatch initial event
        window.dispatchEvent(new CustomEvent('gridColumnsChange', { 
            detail: { columns: isDesktop() ? 2 : 1 } 
        }))
    }, [])

    const handleGridChange = (columns) => {
        setGridColumns(columns)
        window.dispatchEvent(new CustomEvent('gridColumnsChange', { detail: { columns } }))
    }

    const docCount = response.products?.total ?? 0
    const totalSummary = `${docCount} results`

    const { activeSort, setSort } = useSort(sortOptions)

    const handleSortChange = event => {
        setSort(event.target.value)
    }

    const options = sortOptions.map(o => ({ value: o.id, label: o.value.name }))

    return (
        <>
            <div id="searchspring-toolbar-header" class="facets facets-vertical-sort small-hide medium-hide">
                <div class="facets-vertical-form page-width" id="FacetSortForm">
                    <label id="HideFilters" for={toggleButtonId} class="font-primary heading-2-md">
                        <Icon name="sliders" />
                        Hide Filter
                    </label>
                    <div class="facet-filters sorting">
                        <div class="product-count-vertical" role="status">
                            <h2 class="product-count__text">
                                <span id="ProductCountDesktop" class="font-secondary mono-md">
                                    {totalSummary}
                                </span>
                            </h2>
                        </div>

                        {/* Grid Toggle - Desktop: 2 col & 3 col */}
                        {isNewLayout && (
                            <div class="grid-toggle">
                                <button
                                    type="button"
                                    class={`grid-toggle__btn ${gridColumns === 2 ? 'is-active' : ''}`}
                                    onClick={() => handleGridChange(2)}
                                    aria-label="2 column view"
                                    aria-pressed={gridColumns === 2}
                                >
                                    <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect y="12" width="12" height="12" transform="rotate(-90 0 12)" fill="currentColor"/>
                                    <rect x="13" y="12" width="12" height="12" transform="rotate(-90 13 12)" fill="currentColor"/>
                                    <rect y="25" width="12" height="12" transform="rotate(-90 0 25)" fill="currentColor"/>
                                    <rect x="13" y="25" width="12" height="12" transform="rotate(-90 13 25)" fill="currentColor"/>
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    class={`grid-toggle__btn ${gridColumns === 3 ? 'is-active' : ''}`}
                                    onClick={() => handleGridChange(3)}
                                    aria-label="3 column view"
                                    aria-pressed={gridColumns === 3}
                                >
                                    <svg width="38" height="25" viewBox="0 0 38 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect y="12" width="12" height="12" transform="rotate(-90 0 12)" fill="currentColor"/>
                                    <rect x="13" y="12" width="12" height="12" transform="rotate(-90 13 12)" fill="currentColor"/>
                                    <rect x="26" y="12" width="12" height="12" transform="rotate(-90 26 12)" fill="currentColor"/>
                                    <rect y="25" width="12" height="12" transform="rotate(-90 0 25)" fill="currentColor"/>
                                    <rect x="13" y="25" width="12" height="12" transform="rotate(-90 13 25)" fill="currentColor"/>
                                    <rect x="26" y="25" width="12" height="12" transform="rotate(-90 26 25)" fill="currentColor"/>
                                    </svg>
                                </button>
                            </div>
                        )}

                        <div class="facet-filters__field">
                            <div class="select">
                                <Icon name="sort-big" />
                                <label for="SortBy" class="facet-filters__label font-primary heading-2-md">
                                    Sort
                                </label>
                                <Select
                                    value={activeSort}
                                    onChange={e => handleSortChange(e)}
                                    options={options}
                                    label={"Sort by"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="facets-container ss__facets__container__mobile">
                <div class="product-count light large-up-hide" role="status">
                    <h2 class="product-count__text font-secondary mono-md">
                        <span id="ProductCount">{totalSummary}</span>
                    </h2>
                </div>
                <div class="mobile-facets__wrapper large-up-hide" data-breakpoint="mobile">
                    <div class="mobile-facets__disclosure disclosure-has-popup">
                        <label for={toggleButtonId} class="mobile-facets__open-wrapper focus-offset">
                            <span role="button" class="mobile-facets__open-button font-primary heading-2-md">
                                <Icon name="sliders" />
                                Filter &amp; Sort
                            </span>
                        </label>
                    </div>

                    {/* Grid Toggle - Mobile: 1 col & 2 col */}
                    {isNewLayout && (
                        <div class="grid-toggle grid-toggle--mobile">
                            <button
                                type="button"
                                class={`grid-toggle__btn ${gridColumns === 1 ? 'is-active' : ''}`}
                                onClick={() => handleGridChange(1)}
                                aria-label="1 column view"
                                aria-pressed={gridColumns === 1}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="16" height="7" fill="currentColor"/>
                                    <rect y="9" width="16" height="7" fill="currentColor"/>
                                </svg>
                            </button>
                            <button
                                type="button"
                                class={`grid-toggle__btn ${gridColumns === 2 ? 'is-active' : ''}`}
                                onClick={() => handleGridChange(2)}
                                aria-label="2 column view"
                                aria-pressed={gridColumns === 2}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="7" height="7" fill="currentColor"/>
                                    <rect x="9" width="7" height="7" fill="currentColor"/>
                                    <rect y="9" width="7" height="7" fill="currentColor"/>
                                    <rect x="9" y="9" width="7" height="7" fill="currentColor"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}